import React, { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import { FUNDING_RIGHT_DRAWER_CLASS } from './fundingDrawerUi';
import {
  clientDataFromReviewFields,
  groupReviewFieldsByCategory,
} from '../../../utils/fundingClientIntakeUtils';
import {
  EXTRACTION_PARTIAL_MANUAL_HINT,
  parsePastedClientInfo,
  PASTE_PLACEHOLDER,
  PASTED_SOURCE,
  PASTE_PARTIAL_TEXT_ONLY_MESSAGE,
} from '../../../services/funding/fundingClientPasteService';
import { EXTRACTION_STATUS } from '../../../services/funding/documentExtractionService';
import {
  hasMinimumExtractedClientData,
} from './UploadDocumentsDrawer';

const STEPS = [
  { id: 'paste', label: '1. Paste info' },
  { id: 'review', label: '2. Review data' },
  { id: 'create', label: '3. Create client' },
];

export const PASTE_SAVE_MODES = [
  {
    id: 'create_and_save',
    title: 'Create client and save pasted info',
    description: 'Creates a new client from extracted fields and saves the pasted info as a reference note.',
  },
  {
    id: 'create_info_only',
    title: 'Create client using extracted info only',
    description: 'Creates a new client from extracted fields. Pasted info will not be saved as a reference note.',
  },
  {
    id: 'manual',
    title: 'Continue with manual intake',
    description: 'Opens manual intake with extracted fields prefilled where available.',
  },
  {
    id: 'discard',
    title: 'Discard pasted info',
    description: 'Clears pasted data and returns to the paste step.',
  },
];

function getPasteSaveModes({ hasExtractedFields }) {
  if (!hasExtractedFields) {
    return PASTE_SAVE_MODES.filter((mode) => mode.id === 'manual' || mode.id === 'discard');
  }
  return PASTE_SAVE_MODES;
}

function resolvePasteDefaultSaveMode({ hasExtractedFields, hasMinimumFields }) {
  const available = getPasteSaveModes({ hasExtractedFields });
  if (hasExtractedFields && hasMinimumFields && available.some((mode) => mode.id === 'create_and_save')) {
    return 'create_and_save';
  }
  if (!hasExtractedFields && available.some((mode) => mode.id === 'manual')) {
    return 'manual';
  }
  return available[0]?.id || 'discard';
}

const PasteClientInfoDrawer = ({
  open,
  onOpenChange,
  onCreateClient,
  onBackToOptions,
  onContinueManualIntake,
  agencyId = null,
  initialText = '',
}) => {
  const [pastedText, setPastedText] = useState('');
  const [workflowStep, setWorkflowStep] = useState('paste');
  const [parsing, setParsing] = useState(false);
  const [reviewFields, setReviewFields] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [saveMode, setSaveMode] = useState('create_and_save');
  const [extractionStatus, setExtractionStatus] = useState(null);
  const [extractionMessage, setExtractionMessage] = useState('');
  const [textPreview, setTextPreview] = useState('');
  const [extractionWarnings, setExtractionWarnings] = useState([]);
  const [textPreviewOpen, setTextPreviewOpen] = useState(true);

  const resetState = () => {
    setPastedText('');
    setWorkflowStep('paste');
    setParsing(false);
    setReviewFields([]);
    setClientData(null);
    setSaveMode('create_and_save');
    setExtractionStatus(null);
    setExtractionMessage('');
    setTextPreview('');
    setExtractionWarnings([]);
    setTextPreviewOpen(true);
  };

  React.useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (initialText) {
      setPastedText(initialText);
    }
  }, [open, initialText]);

  const hasExtractedFields = extractionStatus === EXTRACTION_STATUS.EXTRACTED
    || extractionStatus === EXTRACTION_STATUS.PARTIAL;
  const extractionPartial = extractionStatus === EXTRACTION_STATUS.PARTIAL;
  const hasMinimumFields = useMemo(
    () => hasMinimumExtractedClientData(reviewFields, clientData),
    [reviewFields, clientData],
  );

  const saveModeOptions = useMemo(() => ({
    hasExtractedFields,
    hasMinimumFields,
  }), [hasExtractedFields, hasMinimumFields]);

  const availableSaveModes = useMemo(
    () => getPasteSaveModes(saveModeOptions),
    [saveModeOptions],
  );

  const hasUsefulFields = reviewFields.some((row) => row.extracted);

  const groupedReviewFields = useMemo(
    () => groupReviewFieldsByCategory(reviewFields.filter((row) => row.extracted)),
    [reviewFields],
  );

  const showTextOnlyPartial = extractionPartial && !reviewFields.length && Boolean(textPreview);
  const activeStepIndex = workflowStep === 'paste' ? 0 : 2;

  const handleParse = async () => {
    if (!pastedText.trim()) {
      toast.error('Paste client info before parsing');
      return;
    }

    setParsing(true);
    const result = await parsePastedClientInfo(pastedText, { agencyId });
    setParsing(false);
    setReviewFields(result.fields || []);
    setClientData(result.clientData);
    setExtractionStatus(result.status);
    setExtractionMessage(result.message || '');
    setTextPreview(result.textPreview || pastedText.slice(0, 500));
    setExtractionWarnings(result.warnings || []);
    setTextPreviewOpen(true);
    setWorkflowStep('review');
    setSaveMode(resolvePasteDefaultSaveMode({
      hasExtractedFields: result.status === EXTRACTION_STATUS.EXTRACTED
        || result.status === EXTRACTION_STATUS.PARTIAL,
      hasMinimumFields: hasMinimumExtractedClientData(result.fields || [], result.clientData),
    }));

    if (result.status === EXTRACTION_STATUS.EXTRACTED && result.fields?.length) {
      toast.success('Pasted info parsed');
    } else if (result.status === EXTRACTION_STATUS.PARTIAL) {
      toast.warning(result.detail || 'Partial extraction. Review before creating client.');
    } else {
      toast.info(result.message || 'Could not parse all fields. Continue manually if needed.');
    }
  };

  const handleContinueManual = () => {
    onContinueManualIntake?.({
      pastedText,
      notes: pastedText.trim(),
    });
    onOpenChange(false);
  };

  const handleFinalize = () => {
    if (saveMode === 'discard') {
      resetState();
      setWorkflowStep('paste');
      toast.info('Pasted info cleared');
      return;
    }

    if (saveMode === 'manual' || !hasUsefulFields) {
      onContinueManualIntake?.({
        pastedText,
        notes: pastedText.trim(),
        reviewFields,
        saveMode,
      });
      onOpenChange(false);
      return;
    }

    if (!hasMinimumFields) {
      onContinueManualIntake?.({ pastedText, notes: pastedText.trim(), reviewFields, saveMode });
      onOpenChange(false);
      return;
    }

    const merged = clientDataFromReviewFields(reviewFields, clientData || {});
    onCreateClient?.({
      clientData: merged,
      documents: [],
      saveMode,
      reviewFields: reviewFields.filter((row) => row.apply),
      saveDocuments: false,
      infoOnly: saveMode === 'create_info_only',
      pastedText,
      source: PASTED_SOURCE,
      savePastedReference: saveMode === 'create_and_save',
    });
    onOpenChange(false);
  };

  const primaryActionLabel = (() => {
    if (saveMode === 'discard') return 'Discard pasted info';
    if (saveMode === 'manual' || !hasUsefulFields) return 'Continue with manual intake';
    return 'Create client from extraction';
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className={`${FUNDING_RIGHT_DRAWER_CLASS} fg-paste-drawer w-full sm:max-w-3xl`}
        data-testid="paste-client-info-drawer"
      >
        <SheetHeader>
          <button
            type="button"
            className="fg-upload-back-btn"
            onClick={onBackToOptions}
            data-testid="paste-back-to-options"
          >
            <ArrowLeft className="size-4" />
            Back to options
          </button>
          <SheetTitle>Paste Client Info</SheetTitle>
          <SheetDescription>
            Paste client details from email, notes, SMS, or OCR text. FundGenius extracts fields for review before creating the client.
          </SheetDescription>
        </SheetHeader>

        <div className="fg-upload-drawer__body">
          <div className="fg-upload-workflow" aria-label="Paste workflow">
            {STEPS.map((step, index) => (
              <span
                key={step.id}
                className={`fg-upload-workflow__step ${index === activeStepIndex ? 'is-active' : ''} ${index < activeStepIndex ? 'is-done' : ''}`}
              >
                {index < activeStepIndex ? '✓ ' : ''}
                {step.label}
              </span>
            ))}
          </div>

          {workflowStep === 'paste' ? (
            <div className="fg-paste-panel" data-testid="paste-client-textarea-panel">
              <label htmlFor="paste-client-info-textarea" className="text-sm font-medium">
                Client info
              </label>
              <textarea
                id="paste-client-info-textarea"
                className="fg-paste-textarea"
                data-testid="paste-client-info-textarea"
                rows={16}
                value={pastedText}
                placeholder={PASTE_PLACEHOLDER}
                onChange={(e) => setPastedText(e.target.value)}
              />
            </div>
          ) : null}

          {workflowStep === 'review' || workflowStep === 'create' ? (
            <>
              <div className="fg-extract-preview" data-testid="paste-extracted-fields-preview">
                <h4>Review extracted fields</h4>
                {extractionPartial ? (
                  <p className="text-sm text-amber-700 dark:text-amber-200" data-testid="paste-partial-warning">
                    {showTextOnlyPartial
                      ? EXTRACTION_PARTIAL_MANUAL_HINT
                      : (extractionMessage || PASTE_PARTIAL_TEXT_ONLY_MESSAGE)}
                  </p>
                ) : null}
                {showTextOnlyPartial ? (
                  <details
                    className="fg-extract-text-preview"
                    data-testid="paste-extracted-text-preview"
                    open={textPreviewOpen}
                    onToggle={(event) => setTextPreviewOpen(event.currentTarget.open)}
                  >
                    <summary>Extracted text preview</summary>
                    <pre>{textPreview}</pre>
                    {extractionWarnings.length ? (
                      <ul className="fg-extract-text-preview__warnings">
                        {extractionWarnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    ) : null}
                  </details>
                ) : null}
                {groupedReviewFields.length ? (
                  <div className="fg-extract-review-table-wrap" data-testid="paste-review-table">
                    {groupedReviewFields.map(({ category, fields }) => (
                      <div key={category} className="fg-extract-review-group" data-testid={`paste-review-group-${category.toLowerCase()}`}>
                        <h5 className="fg-extract-review-group__title">{category}</h5>
                        <table className="fg-extract-review-table">
                          <thead>
                            <tr>
                              <th>Field</th>
                              <th>Current</th>
                              <th>Extracted</th>
                              <th>Source</th>
                              <th>Confidence</th>
                              <th>Apply</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fields.map((row) => (
                              <tr key={row.id}>
                                <td>{row.field}</td>
                                <td>{row.current}</td>
                                <td>{row.extracted}</td>
                                <td>{row.source}</td>
                                <td>{row.confidence != null ? `${row.confidence}%` : '—'}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(row.apply)}
                                    aria-label={`Apply ${row.field}`}
                                    onChange={(e) => {
                                      setReviewFields((prev) => prev.map((item) => (
                                        item.id === row.id ? { ...item, apply: e.target.checked } : item
                                      )));
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--funding-text-secondary)]">
                    Structured fields were not parsed automatically. Continue with manual intake or edit the pasted text.
                  </p>
                )}
              </div>

              {hasExtractedFields ? (
                <div className="fg-upload-save-modes" data-testid="paste-save-modes">
                  <h4>What should FundGenius do with this info?</h4>
                  <div className="fg-upload-save-modes__grid">
                    {PASTE_SAVE_MODES.map((mode) => {
                      const enabled = availableSaveModes.some((item) => item.id === mode.id);
                      return (
                        <label
                          key={mode.id}
                          className={`fg-upload-save-mode ${saveMode === mode.id ? 'is-selected' : ''} ${enabled ? '' : 'is-disabled'}`}
                          data-testid={`paste-save-mode-${mode.id}`}
                        >
                          <input
                            type="radio"
                            name="paste-save-mode"
                            value={mode.id}
                            checked={saveMode === mode.id}
                            disabled={!enabled}
                            onChange={() => {
                              setSaveMode(mode.id);
                              setWorkflowStep('create');
                            }}
                          />
                          <strong>{mode.title}</strong>
                          <span>{mode.description}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="funding-upload-drawer__footer funding-drawer-footer">
          {workflowStep === 'paste' ? (
            <>
              <button
                type="button"
                className="funding-action-button funding-action-button--muted"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="funding-action-button funding-action-button--secondary"
                data-testid="paste-continue-manually-btn"
                onClick={handleContinueManual}
              >
                Continue manually
              </button>
              <button
                type="button"
                className="funding-action-button funding-action-button--primary"
                data-testid="parse-pasted-info-btn"
                disabled={parsing || !pastedText.trim()}
                onClick={handleParse}
              >
                {parsing ? 'Parsing pasted info…' : 'Parse pasted info'}
              </button>
            </>
          ) : null}
          {workflowStep === 'review' || workflowStep === 'create' ? (
            <>
              <button
                type="button"
                className="funding-action-button funding-action-button--muted"
                onClick={() => setWorkflowStep('paste')}
                data-testid="paste-back-to-text-btn"
              >
                Back to paste
              </button>
              {(
                <button
                  type="button"
                  className="funding-action-button funding-action-button--primary"
                  data-testid={primaryActionLabel === 'Continue with manual intake'
                    ? 'paste-continue-manual-intake-btn'
                    : 'paste-create-client-btn'}
                  onClick={handleFinalize}
                >
                  {primaryActionLabel}
                </button>
              )}
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PasteClientInfoDrawer;
